import React, { useCallback, useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  TutorQuizOptionDTO,
  TutorQuizQuestionDTO,
  TutorQuizReviewItemDTO,
  acceptReviewQuiz,
  acceptAllReviewQuizzes,
  listQuizReview,
  rejectReviewQuiz,
} from '../../../services/tutorQuizService';

interface Props {
  courseId: number;
}

function OptionLine({ opt }: { opt: TutorQuizOptionDTO }) {
  return (
    <Box
      sx={{
        px: 1.5, py: 0.5, borderRadius: 1, mb: 0.5,
        bgcolor: opt.isCorrect ? 'success.light' : 'grey.100',
        border: opt.isCorrect ? '1.5px solid' : '1px solid',
        borderColor: opt.isCorrect ? 'success.main' : 'grey.300',
      }}
    >
      <Typography
        variant="body2"
        sx={{ fontWeight: opt.isCorrect ? 700 : 400, color: opt.isCorrect ? 'success.dark' : 'text.primary' }}
      >
        {opt.isCorrect ? '✓ ' : ''}{opt.optionText}
      </Typography>
    </Box>
  );
}

function QuestionBlock({ q, index }: { q: TutorQuizQuestionDTO; index: number }) {
  return (
    <Box mb={1.5}>
      <Stack direction="row" spacing={1} alignItems="flex-start" mb={0.5}>
        <Typography variant="caption" fontWeight={600}>{index + 1}.</Typography>
        <Chip label={q.questionType === 'TRUE_FALSE' ? 'T/F' : 'MC'} size="small" />
        <Typography variant="body2">{q.questionText}</Typography>
      </Stack>
      <Box ml={3}>
        {q.options?.map((o, i) => <OptionLine key={o.id ?? i} opt={o} />)}
        {q.explanation && (
          <Typography variant="caption" color="text.secondary">Explanation: {q.explanation}</Typography>
        )}
      </Box>
    </Box>
  );
}

function ReviewCard({ item, onAccept, onReject }: {
  item: TutorQuizReviewItemDTO;
  onAccept: (id: number) => void;
  onReject: (id: number) => void;
}) {
  const [busy, setBusy] = useState(false);

  async function handle(fn: () => Promise<void>) {
    setBusy(true);
    try { await fn(); } finally { setBusy(false); }
  }

  return (
    <Accordion disableGutters>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1} alignItems="center" flex={1}>
          <Typography variant="body2" fontWeight={500} flex={1}>
            {item.nodeLabel} — {item.title}
          </Typography>
          <Chip label={`${item.questionCount} Q`} size="small" />
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        {item.questions?.map((q, i) => <QuestionBlock key={q.id ?? i} q={q} index={i} />)}
        <Stack direction="row" spacing={1} justifyContent="flex-end" mt={1}>
          <Button
            size="small" variant="outlined" color="error"
            disabled={busy}
            onClick={() => handle(() => rejectReviewQuiz(item.courseId ?? 0, item.quizId!)
              .then(() => onReject(item.quizId!)))}
          >
            Reject
          </Button>
          <Button
            size="small" variant="contained"
            disabled={busy}
            startIcon={busy ? <CircularProgress size={14} /> : undefined}
            onClick={() => handle(() => acceptReviewQuiz(item.courseId ?? 0, item.quizId!)
              .then(() => onAccept(item.quizId!)))}
          >
            Accept
          </Button>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

export default function TutorQuizReviewQueue({ courseId }: Props) {
  const [items, setItems] = useState<TutorQuizReviewItemDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [acceptingAll, setAcceptingAll] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setItems(await listQuizReview(courseId));
    } catch {
      setError('Failed to load review queue.');
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => { load(); }, [load]);

  function removeItem(quizId: number) {
    setItems(prev => prev.filter(i => i.quizId !== quizId));
  }

  async function handleAcceptAll() {
    setAcceptingAll(true);
    try {
      await acceptAllReviewQuizzes(courseId);
      setItems([]);
    } catch {
      setError('Accept all failed.');
    } finally {
      setAcceptingAll(false);
    }
  }

  if (loading) return <Box display="flex" justifyContent="center" py={3}><CircularProgress size={28} /></Box>;

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {items.length > 0 && (
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="body2" color="text.secondary">
            {items.length} quiz{items.length !== 1 ? 'zes' : ''} pending review
          </Typography>
          <Button
            variant="outlined" size="small"
            disabled={acceptingAll}
            startIcon={acceptingAll ? <CircularProgress size={14} /> : undefined}
            onClick={handleAcceptAll}
          >
            Accept All
          </Button>
        </Stack>
      )}

      {items.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No quizzes pending review.</Typography>
      ) : (
        <Stack spacing={1}>
          {items.map(item => (
            <ReviewCard
              key={item.quizId}
              item={{ ...item, courseId }}
              onAccept={id => removeItem(id)}
              onReject={id => removeItem(id)}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
}
